class Carousel extends HTMLElement {
  constructor() {
    super();

    this.slider = this.querySelector(".js-carousel-slider");
    this.enableSliderLooping = this.getAttribute("data-slider-looping") === "true";
    this.enableSliderAutoplay = this.getAttribute("data-slider-autoplay") === "true";
    this.autoplaySpeed = this.getAttribute("data-slider-autoplay-speed")
      ? parseFloat(this.getAttribute("data-slider-autoplay-speed"))
      : 3000;
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.trackers = this.querySelectorAll(".js-carousel-tracker");
    this.progress = this.querySelectorAll(".js-carousel-progress span");
    this.thumbSlider = this.getAttribute("data-slider-thumb")
      ? document.getElementById(this.getAttribute("data-slider-thumb"))
      : null;
    this.fullSlides = this.getAttribute("data-slider-items") === "1";
    this.delayInit = this.getAttribute("data-delay-init") === "true";

    if (!this.slider) return;

    if (this.delayInit) this.addEventListener("trigger-init", () => this.initPages());
    else this.initPages();

    const resizeObserver = new ResizeObserver(() => this.initPages());

    resizeObserver.observe(this.slider);
  }

  initPages() {
    this.sliderItems = this.querySelectorAll(".js-carousel-slide");
    this.slider.addEventListener("scroll", this.update.bind(this));

    if (this.prevButton) {
      this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    }

    if (this.nextButton) {
      this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
    }

    if (this.enableSliderAutoplay) this.autoplay();

    if (this.getAttribute("data-slider-for")) {
      const mainSlider = document.getElementById(this.getAttribute("data-slider-for"));

      this.sliderItems.forEach((slide) => {
        slide.addEventListener("click", () => {
          mainSlider.scrollToSlide(slide.getAttribute("data-slide-index"));
        });
      });
    }

    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0
    );
    if (this.sliderItemsToShow[1] && this.sliderItemsToShow[0]) {
      this.sliderItemOffset =
        this.sliderItemsToShow[1].offsetLeft -
        this.sliderItemsToShow[0].offsetLeft;
      this.slidesPerPage = Math.floor(this.slider.clientWidth / this.sliderItemOffset) + 1;

      if (this.classList.contains("carousel--show-to-edge")) this.slidesPerPage -= 1;

      this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    }

    if (this.fullSlides) this.slidesPerPage = 1;
    if (this.trackers.length) this.trackers.forEach(tracker => this.initTracker(tracker));

    this.update();
  }

  update() {
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    let currentSlide = this.currentPage;

    this.sliderItemsToShow.forEach((item, index) => {
      let thisSlide = index + 1;

      if (this.isSlideVisible(item) && thisSlide < currentSlide) {
        currentSlide = thisSlide;
      }
    });

    if (!this.enableSliderLooping) {
      if (this.prevButton) {
        if (this.isSlideVisible(this.sliderItemsToShow[0])) {
          this.prevButton.setAttribute("disabled", "disabled");
        } else {
          this.prevButton.removeAttribute("disabled");
        }
      }

      if (this.nextButton) {
        if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])
        ) {
          this.nextButton.setAttribute("disabled", "disabled");
        } else {
          this.nextButton.removeAttribute("disabled");
        }
      }
    }

    if (this.trackers) this.trackers.forEach(tracker => this.updateTracker(currentSlide, tracker));
    if (this.thumbSlider) this.updateThumb(currentSlide);
    if (this.progress) this.updateScrollProgress();

    this.initDraggable();
  }

  isSlideVisible(element, offset = 0) {
    if (element) {
      const lastVisibleSlide = Math.ceil(
        this.slider.clientWidth + this.slider.scrollLeft - offset
      );
      return (
        element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
        element.offsetLeft >= this.slider.scrollLeft
      );
    }
  }

  initTracker(tracker) {
    //init dots if element is present in the carousel
    //dots are outputted per slide but then hidden as there might be multiple items per slide
    let allParts = tracker.querySelectorAll("div");

    if (!allParts.length) {
      for (let index = 0; index < this.sliderItems.length; index++) {
        tracker.innerHTML += '<div class="carousel__tracker-part"></div>';
      }
    }

    allParts = tracker.querySelectorAll("div");

    for (let index = 0; index < allParts.length; index++) {
      if (!this.fullSlides) {
        if (index >= this.totalPages) {
          allParts[index].style.display = "none";
        } else if (
          index === this.totalPages - 1 &&
          window.innerWidth > 2000 &&
          this.classList.contains("carousel--show-to-edge")
        ) {
          allParts[index].style.display = "none";
        }
      } else {
        tracker.querySelectorAll("div")[0].classList.add("active");
      }

      allParts[index].addEventListener("click", () => this.scrollToSlide(index + 1))
    }
  }

  updateTracker(slide, tracker) {
    // Updates active part to display which slide is visible
    const allParts = tracker.querySelectorAll("div");

    allParts.forEach(part => part.classList.remove("active"));

    if (allParts[slide - 1]) allParts[slide - 1].classList.add("active");
  }

  updateThumb(slide) {
    const thumbSlides = this.thumbSlider.querySelectorAll(
      ".carousel__slides-slide"
    );

    if (thumbSlides.length) {
      for (let index = 0; index < thumbSlides.length; index++) {
        let slideNumber = slide - 1;
        if (index === slideNumber) {
          thumbSlides[index].classList.add("active");
        } else {
          thumbSlides[index].classList.remove("active");
        }
      }

      if (typeof this.thumbSlider.scrollToSlide == "undefined") return;

      this.thumbSlider.scrollToSlide(slide);
    }
  }

  scrollToSlide(slide) {
    //check if slide is in view, if not, scroll to it
    if (!this.isSlideVisible(this.sliderItemsToShow[slide - 1])) {
      this.slideScrollPosition = (slide - 1) * this.sliderItemOffset;
      this.slider.scrollTo({
        left: this.slideScrollPosition,
      });
    }
  }

  initDraggable() {
    let isDown = false;
    let startX;
    let scrollLeft;

    this.slider.addEventListener('mousedown', event => {
      event.preventDefault();

      isDown = true;

      this.slider.classList.add('swiping');

      startX = event.pageX - this.slider.offsetLeft;
      scrollLeft = this.slider.scrollLeft;
    });

    this.slider.addEventListener('mouseup', event => {
      event.preventDefault();

      isDown = false;

      this.setLinkAction('auto');

      this.slider.classList.remove('swiping');
    });

    this.slider.addEventListener('mousemove', event => {
      if (!isDown) return;

      this.setLinkAction('none');

      const x = event.pageX - this.slider.offsetLeft;
      const walk = (x - startX) * 3;

      this.slider.scrollLeft = scrollLeft - walk;
    });
  }

  setLinkAction(pointer) {
    this.sliderItems.forEach(slide => {
      const links = slide.querySelectorAll("a");

      links.forEach(link => link.style.pointerEvents = pointer);
    });
  }

  onButtonClick(event, autoplay = false) {
    if (!autoplay) event.preventDefault();

    const step = event?.currentTarget?.dataset?.step || 1;

    this.slideScrollPosition =
      event.currentTarget.name === "next"
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;

    if (
      this.currentPage === 1 &&
      event.currentTarget.name === "previous" &&
      this.enableSliderLooping
    ) {
      this.slider.scrollTo({ left: this.sliderItemsToShow.length * this.sliderItemOffset });
    } else if (
      this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1]) &&
      event.currentTarget.name === "next" && this.enableSliderLooping
    ) {
      this.slider.scrollLeft = 0;
    } else {
      this.slider.scrollTo({ left: this.slideScrollPosition });
    }
  }

  autoplay() {
    setInterval(() => this.onButtonClick({ currentTarget: { name: "next" }}, true), this.autoplaySpeed);
  }

  updateScrollProgress() {
    const totalScroll = this.slider.scrollWidth - this.slider.clientWidth;
    const currentScroll = this.slider.scrollLeft;
    const percentagePosition = (currentScroll / totalScroll) * 100;

    if (percentagePosition) this.progress.forEach(bar => bar.style.width = percentagePosition + '%');
  }
}

customElements.define("carousel-slider", Carousel);
